import type {
  PlayCrm,
  TriggerConfig,
  TriggerKind,
} from '@/types/custom-playbooks';
import { isAttioId } from './crm-fields';

export function isDealKind(kind: TriggerKind | undefined): boolean {
  return kind === 'deal_stage' || kind === 'deal_stalled';
}

/** Mirror the closed native Deal config, without inheriting ordinary CRM filters. */
export function dealTriggerIssue(
  config: Partial<TriggerConfig>
): string | null {
  const crm = config.crm;
  if (
    config.source !== 'crm' ||
    !isDealKind(config.kind) ||
    !crm ||
    !['salesforce', 'hubspot', 'attio'].includes(crm.crmType) ||
    crm.objectType !== 'Deal' ||
    crm.conditionType !==
      (config.kind === 'deal_stage' ? 'stage_change' : 'stalled')
  )
    return 'Pick the matching Deal trigger and CRM.';
  const allowed = new Set([
    'crmType',
    'objectType',
    'conditionType',
    'stages',
    'pipelineId',
    'amountMin',
    'stalledDays',
    'backfill',
  ]);
  if (Object.keys(crm).some((key) => !allowed.has(key)))
    return 'Remove inherited CRM filters from this Deal trigger.';
  if (
    config.rerunAfterDays !== undefined ||
    config.skipTouchedWithinDays !== undefined
  )
    return 'Deal triggers do not repeat or skip records by a record window.';
  const stages = crm.stages ?? [];
  const text = (value: unknown) =>
    typeof value === 'string' &&
    !!value.trim() &&
    value.length <= 100 &&
    !value.includes('\0');
  if (
    !Array.isArray(stages) ||
    stages.length > 20 ||
    (config.kind === 'deal_stage' && !stages.length) ||
    stages.some((stage) => !text(stage)) ||
    new Set(stages).size !== stages.length
  )
    return 'Pick one to 20 distinct native stages.';
  if (crm.crmType === 'attio') {
    // Attio doesn't say which stages are closed, so a stall counts only the
    // stages picked as open. Stages compare by status id (deal_trigger.py).
    if (!stages.length)
      return 'Pick the open stages. Attio does not say which deal stages are closed.';
    if (!stages.every(isAttioId)) return 'Pick Attio stages from the list.';
    if (crm.pipelineId != null)
      return 'Attio deal stages have no pipeline. Remove it.';
  } else if (
    crm.pipelineId != null &&
    (!text(crm.pipelineId) || crm.crmType !== 'hubspot')
  )
    return 'Pick a native HubSpot pipeline, or any pipeline.';
  if (
    crm.amountMin != null &&
    (typeof crm.amountMin !== 'number' ||
      !Number.isFinite(crm.amountMin) ||
      crm.amountMin < 0)
  )
    return 'Minimum amount must be a nonnegative number.';
  if (crm.backfill !== undefined && !['now', 'historic'].includes(crm.backfill))
    return 'Choose new stage entries or include existing entries.';
  if (config.kind === 'deal_stalled') {
    if (
      !Number.isInteger(crm.stalledDays) ||
      (crm.stalledDays ?? 0) < 1 ||
      (crm.stalledDays ?? 0) > 365
    )
      return 'Use one to 365 days without activity.';
    if (!config.schedule || config.schedule.frequency === 'continuous')
      return 'Choose a schedule for stalled deals.';
  } else if (crm.stalledDays != null)
    return 'Days without activity apply only to stalled deals.';
  return null;
}

/** An explicit edit writes only supported keys and clears unsupported windows. */
export function dealConfig(
  config: Partial<TriggerConfig>,
  kind: 'deal_stage' | 'deal_stalled',
  crmType: PlayCrm
): TriggerConfig {
  const previous = config.crm;
  const sameCrm =
    previous?.crmType === crmType && previous.objectType === 'Deal';
  return {
    source: 'crm',
    kind,
    crm: {
      crmType,
      objectType: 'Deal',
      conditionType: kind === 'deal_stage' ? 'stage_change' : 'stalled',
      stages: sameCrm ? (previous.stages ?? []) : [],
      // Only HubSpot has pipelines; Attio refuses one (deal_trigger.py).
      pipelineId:
        sameCrm && crmType === 'hubspot' ? (previous.pipelineId ?? null) : null,
      ...(sameCrm && previous.amountMin != null
        ? { amountMin: previous.amountMin }
        : {}),
      ...(kind === 'deal_stalled'
        ? { stalledDays: sameCrm ? (previous.stalledDays ?? 14) : 14 }
        : {}),
      backfill: sameCrm ? (previous.backfill ?? 'now') : 'now',
    },
    schedule: config.schedule,
  };
}
